import React, {useState, useEffect} from "react";
import {Button, Container, Row, Col} from "reactstrap";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import {useNavigate} from "react-router-dom";
import {useUser} from "../contexts/AuthContext";
import ericImg from "../assets/img/vendors/default/eric.jpg";
import simonImg from "../assets/img/vendors/default/simon.jpg";
import llamaImg from "../assets/img/vendors/default/llama.jpg";
import tariqImg from "../assets/img/vendors/default/tariq.jpg";
import daveImg from "../assets/img/vendors/default/dave.jpg";
import rayImg from "../assets/img/vendors/default/ray.png";

const Homepage: React.FC = () => {
  const navigate = useNavigate();
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  useUser();

  // Feature carousel images - numbered screenshots
  const featureImages = [
    "/img/screenshots/01.webp",
    "/img/screenshots/02.webp",
    "/img/screenshots/03.webp",
    "/img/screenshots/04.webp",
    "/img/screenshots/05.webp",
    "/img/screenshots/06.webp",
    "/img/screenshots/07.webp"
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCarouselIndex((prev) => (prev + 1) % featureImages.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [featureImages.length]);

  const handleGetStarted = () => {
    navigate("/upload");
  };

  const handleShutdownNotice = () => {
    navigate("/shutdown");
  };

  const handleViewDemo = () => {
    navigate("/report/26867c72-b41c-4367-978c-168dca7ed809");
  };

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <div className="homepage-wrapper">
          {/* Hero Section */}
          <section className="marketing-hero">
            <div className="hero-content">
              <p className="hero-pretitle">Zwiftalizer is now</p>
              <h1>TrainerDX</h1>
              <p className="hero-subtitle">The trainer diagnostics expert for indoor cycling</p>
              <p style={{fontSize: "1.2rem", opacity: 0.85, maxWidth: "800px", margin: "0 auto"}}>
                Stop guessing what's wrong. Get instant insights with 43 comprehensive rules analyzing network,
                graphics, sensors, and hardware. Know exactly what's wrong and how to fix it.
              </p>

              <div className="hero-buttons">
                <Button color="primary" size="lg" onClick={handleGetStarted} data-color={color}>
                  <i className="fa fa-upload mr-2"></i>
                  Get Started
                </Button>
                <Button
                  color="secondary"
                  size="lg"
                  outline
                  onClick={handleViewDemo}
                  data-color={color}
                  style={{borderColor: "rgba(255,255,255,0.5)", color: "#fff"}}
                >
                  <i className="fa fa-eye mr-2"></i>
                  View Demo Report
                </Button>
              </div>

              {/* Feature Carousel */}
              <div className="feature-carousel">
                {featureImages.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`TrainerDX screenshot ${index + 1}`}
                    loading="lazy"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      opacity: index === activeCarouselIndex ? 1 : 0,
                      transition: "opacity 1s ease-in-out"
                    }}
                  />
                ))}
                <div className="carousel-indicators">
                  {featureImages.map((_, index) => (
                    <div
                      key={index}
                      className={`indicator ${index === activeCarouselIndex ? "active" : ""}`}
                      onClick={() => setActiveCarouselIndex(index)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Big Stats Section */}
          <section className="big-stats-section">
            <Container>
              <h2 className="section-title">Comprehensive Analysis at Your Fingertips</h2>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">43</div>
                  <div className="stat-label">Analysis Rules</div>
                  <div className="stat-description">Expert-written checks covering every aspect</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">89</div>
                  <div className="stat-label">Intelligent Conditions</div>
                  <div className="stat-description">Intelligent pattern detection with advanced logic</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">67</div>
                  <div className="stat-label">Unique Recommendations</div>
                  <div className="stat-description">Actionable fixes for every issue</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">5</div>
                  <div className="stat-label">Performance Categories</div>
                  <div className="stat-description">Network, Graphics, Sensors, Hardware, Correlation</div>
                </div>
              </div>
            </Container>
          </section>

          {/* Feature Section 1: Network Analysis */}
          <section className="feature-section">
            <Container>
              <div className="feature-content">
                <div className="feature-text">
                  <span className="category-badge badge-network">Network Performance</span>
                  <h2>Stop Riders from Disappearing</h2>
                  <p className="feature-description">
                    13 comprehensive rules detect connection quality issues causing riders to disappear, teleport, or
                    experience lag. From firewall blocks to WiFi interference, we identify the root cause.
                  </p>
                  <ul className="feature-list">
                    <li>TCP disconnections and communication errors</li>
                    <li>UDP packet loss and timeout detection</li>
                    <li>Server connection stability monitoring</li>
                    <li>Latency test failure analysis</li>
                    <li>Firewall and VPN issue identification</li>
                  </ul>
                  <div className="feature-cta">
                    <Button color="primary" onClick={handleGetStarted} data-color={color}>
                      Analyze Your Connection
                    </Button>
                  </div>
                </div>
                <div className="feature-image">
                  <img
                    src="/img/screenshots/ble.webp"
                    alt="Network Analysis Dashboard"
                    loading="lazy"
                    style={{width: "100%", height: "auto", borderRadius: "8px"}}
                  />
                </div>
              </div>
            </Container>
          </section>

          {/* Feature Section 2: Graphics Performance */}
          <section className="feature-section">
            <Container>
              <div className="feature-content reverse">
                <div className="feature-text">
                  <span className="category-badge badge-graphics">Graphics Performance</span>
                  <h2>Eliminate FPS Drops and Stuttering</h2>
                  <p className="feature-description">
                    13 rules identify frame rate issues, CPU bottlenecks, and hardware configuration problems. The #1
                    performance killer? Single-core CPU bottleneck—we detect it instantly.
                  </p>
                  <ul className="feature-list">
                    <li>Frame rate performance analysis (critical &lt;30 FPS)</li>
                    <li>CPU bottleneck detection during high rider counts</li>
                    <li>GPU capability vs. graphics profile mismatches</li>
                    <li>Resolution optimization opportunities</li>
                    <li>Legacy hardware limitation warnings</li>
                  </ul>
                  <div className="feature-cta">
                    <Button color="primary" onClick={handleGetStarted} data-color={color}>
                      Check Graphics Performance
                    </Button>
                  </div>
                </div>
                <div className="feature-image">
                  <img
                    src="/img/screenshots/graphics.webp"
                    alt="Graphics Performance Analysis"
                    loading="lazy"
                    style={{width: "100%", height: "auto", borderRadius: "8px"}}
                  />
                </div>
              </div>
            </Container>
          </section>

          {/* Feature Section 3: Sensor Connectivity */}
          <section className="feature-section">
            <Container>
              <div className="feature-content">
                <div className="feature-text">
                  <span className="category-badge badge-sensors">Sensor Connectivity</span>
                  <h2>Reliable Power and Heart Rate Data</h2>
                  <p className="feature-description">
                    13 rules monitor ANT+ and Bluetooth device reliability. Detect dying batteries, interference from
                    WiFi channel 10, USB 3.0 issues, and connection pattern problems before they ruin your ride.
                  </p>
                  <ul className="feature-list">
                    <li>ANT+ RxFail rate monitoring (warning &gt;15%, critical &gt;30%)</li>
                    <li>Battery low pattern recognition</li>
                    <li>Power meter dropout severity classification</li>
                    <li>Bluetooth disconnection detection</li>
                    <li>FTMS resend rate monitoring</li>
                  </ul>
                  <div className="feature-cta">
                    <Button color="primary" onClick={handleGetStarted} data-color={color}>
                      Analyze Sensor Reliability
                    </Button>
                  </div>
                </div>
                <div className="feature-image">
                  <img
                    src="/img/screenshots/antcharts.webp"
                    alt="Sensor Connectivity Analysis"
                    loading="lazy"
                    style={{width: "100%", height: "auto", borderRadius: "8px"}}
                  />
                </div>
              </div>
            </Container>
          </section>

          {/* Feature Section 4: Advanced Correlation */}
          <section className="feature-section">
            <Container>
              <div className="feature-content reverse">
                <div className="feature-text">
                  <span className="category-badge badge-hardware">Advanced Intelligence</span>
                  <h2>Pattern Detection That Goes Deeper</h2>
                  <p className="feature-description">
                    Beyond simple checks, our 3 advanced correlation patterns analyze relationships between multiple
                    metrics to identify root causes traditional analysis misses.
                  </p>
                  <ul className="feature-list">
                    <li>FPS-Network correlation detects CPU bottleneck disguised as graphics card problem</li>
                    <li>ANT+ battery pattern analysis distinguishes battery vs. interference issues</li>
                    <li>Global ANT+ re-pairing culprit detection pinpoints problematic sensors</li>
                    <li>Multi-metric root cause analysis</li>
                    <li>Context-aware recommendations</li>
                  </ul>
                  <div className="feature-cta">
                    <Button color="primary" onClick={handleGetStarted} data-color={color}>
                      Get Deep Analysis
                    </Button>
                  </div>
                </div>
                <div className="feature-image">
                  <img
                    src="/img/screenshots/advanced-graphics.webp"
                    alt="Advanced Correlation Analysis"
                    loading="lazy"
                    style={{width: "100%", height: "auto", borderRadius: "8px"}}
                  />
                </div>
              </div>
            </Container>
          </section>

          {/* How It Works Section */}
          <section className="big-stats-section" style={{background: "rgba(255,255,255,0.02)"}}>
            <Container>
              <h2 className="section-title">How It Works</h2>
              <Row className="mt-5">
                <Col md="3" className="text-center mb-4">
                  <div style={{fontSize: "3rem", marginBottom: "1rem"}}>
                    <i className="fa fa-upload" style={{color: "#667eea"}}></i>
                  </div>
                  <h4>1. Upload Your Log</h4>
                  <p style={{opacity: 0.8}}>Drag and drop your log file or select it from your computer</p>
                </Col>
                <Col md="3" className="text-center mb-4">
                  <div style={{fontSize: "3rem", marginBottom: "1rem"}}>
                    <i className="fa fa-cogs" style={{color: "#764ba2"}}></i>
                  </div>
                  <h4>2. Instant Analysis</h4>
                  <p style={{opacity: 0.8}}>
                    Our system analyzes 43 performance criteria across all categories in seconds
                  </p>
                </Col>
                <Col md="3" className="text-center mb-4">
                  <div style={{fontSize: "3rem", marginBottom: "1rem"}}>
                    <i className="fa fa-chart-line" style={{color: "#667eea"}}></i>
                  </div>
                  <h4>3. Review Issues</h4>
                  <p style={{opacity: 0.8}}>
                    See prioritized issues with severity ratings, clear explanations, and technical details
                  </p>
                </Col>
                <Col md="3" className="text-center mb-4">
                  <div style={{fontSize: "3rem", marginBottom: "1rem"}}>
                    <i className="fa fa-wrench" style={{color: "#764ba2"}}></i>
                  </div>
                  <h4>4. Apply Fixes</h4>
                  <p style={{opacity: 0.8}}>
                    Follow step-by-step recommendations with difficulty and time estimates for each fix
                  </p>
                </Col>
              </Row>
            </Container>
          </section>

          {/* Testimonials Section */}
          <section className="big-stats-section">
            <Container>
              <h2 className="section-title">What People Are Saying</h2>
              <div style={{maxWidth: "1000px", margin: "0 auto"}}>
                {/* Eric Schlange - Left aligned */}
                <div className="testimonial-item">
                  <div className="testimonial-image">
                    <img src={ericImg} alt="Eric Schlange" />
                  </div>
                  <div className="testimonial-text">
                    <blockquote>
                      "A super-handy tool for analyzing your Zwift setup's performance, complete with beautiful charts
                      and tips."
                    </blockquote>
                    <p className="testimonial-author">— Eric Schlange, Zwift Insider Blog</p>
                  </div>
                </div>

                {/* Shane Miller - Right aligned */}
                <div className="testimonial-item reverse">
                  <div className="testimonial-image">
                    <img src={llamaImg} alt="Shane Miller" />
                  </div>
                  <div className="testimonial-text">
                    <blockquote>"One of my favorite websites."</blockquote>
                    <p className="testimonial-author">— Shane Miller, GPLama YouTube channel</p>
                  </div>
                </div>

                {/* Simon Schofield - Left aligned */}
                <div className="testimonial-item">
                  <div className="testimonial-image">
                    <img src={simonImg} alt="Simon Schofield" />
                  </div>
                  <div className="testimonial-text">
                    <blockquote>
                      "An invaluable tool for diagnosing connection and other issues for Zwifters."
                    </blockquote>
                    <p className="testimonial-author">— Simon Schofield, Zwiftcast Podcast</p>
                  </div>
                </div>

                {/* Dave Higgins - Right aligned */}
                <div className="testimonial-item reverse">
                  <div className="testimonial-image">
                    <img src={daveImg} alt="Dave Higgins" />
                  </div>
                  <div className="testimonial-text">
                    <blockquote>
                      "A superb asset for the Zwift community, providing vital diagnosis when troubleshooting and
                      helping PC builders optimise performance."
                    </blockquote>
                    <p className="testimonial-author">— Dave Higgins, ZPCMR Facebook Group</p>
                  </div>
                </div>

                {/* Tariq Ali - Left aligned */}
                <div className="testimonial-item">
                  <div className="testimonial-image">
                    <img src={tariqImg} alt="Tariq Ali" />
                  </div>
                  <div className="testimonial-text">
                    <blockquote>"A useful tool for reading zwift logs."</blockquote>
                    <p className="testimonial-author">— Tariq Ali, Smart Bike Trainers YouTube channel</p>
                  </div>
                </div>

                {/* DC Rainmaker - Right aligned */}
                <div className="testimonial-item reverse">
                  <div className="testimonial-image">
                    <img src={rayImg} alt="Ray Maker" />
                  </div>
                  <div className="testimonial-text">
                    <blockquote>"It's really cool, check it out."</blockquote>
                    <p className="testimonial-author">— D.C. Rainmaker, DCR Podcast</p>
                  </div>
                </div>
              </div>
            </Container>
          </section>

          {/* Final CTA */}
          <section className="final-cta-section">
            <div className="cta-content">
              <h2>TrainerDX is closing on November 30, 2026</h2>
              <p>
                Thank you to everyone who used TrainerDX over the past ten years. Existing accounts can keep analyzing
                logs until the shutdown date.
              </p>
              <Button
                size="lg"
                onClick={handleShutdownNotice}
                style={{
                  background: "white",
                  color: "#667eea",
                  padding: "1.2rem 3rem",
                  fontSize: "1.2rem",
                  fontWeight: "600",
                  borderRadius: "50px",
                  border: "none"
                }}
              >
                <i className="fa fa-info-circle mr-2"></i>
                Read the Shutdown Notice
              </Button>
            </div>
          </section>
        </div>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default Homepage;
